import { Controller } from 'stimulus';
import DesignVisitRecorder from './design_visit_recorder';

const SLIDE_SELECTOR = '[data-design-id]';

// Records the designs a client browses inside the presentation carousel, so the portal's
// New/Updated badges clear for them too: boards#show and fp_views#show only record the
// design the client opened directly.
export default class extends Controller {
  static values = { url: String };

  initialize() {
    this.recorder = new DesignVisitRecorder(this.urlValue);
    this.presentationOpen = false;
    this.shownSlides = new WeakSet();
  }

  disconnect() {
    this.cancel();
  }

  // `ui--presentation:open`. The carousel only announces slides as they change, and on every
  // open after the first it is already sitting on the one it reset to when it last closed, so
  // the design the client lands on has to be picked up from here.
  activate() {
    this.presentationOpen = true;
    this.#scheduleFor(this.currentSlide);
  }

  // `ui--presentation:close`. The carousel resets to its initial slide right after this and
  // announces the change, so clearing the dwell is not enough — the flag is what keeps that
  // reset from re-arming one behind a closed overlay.
  cancel() {
    this.presentationOpen = false;
    this.recorder.cancel();
  }

  // `ui--carousel:slide-shown`. Stored even while closed: the carousel announces the slide
  // it resets to on close, and that is the one the client lands on when they open it again.
  scheduleVisit(event) {
    this.currentSlide = event.detail?.slide;
    this.#scheduleFor(this.currentSlide);
  }

  // `designs--lazy-loading:shown`, bubbling from a thumbnail that has just swapped its
  // generated image in. It often lands on a slide the client has already arrowed past, so it
  // is remembered for when they come back rather than acted on straight away.
  markImageShown(event) {
    const slide = event.target.closest(SLIDE_SELECTOR);
    if (!slide) {
      return;
    }

    this.shownSlides.add(slide);

    if (slide === this.currentSlide) {
      this.#scheduleFor(slide);
    }
  }

  // The dwell clock only starts once the slide shows the design itself, so one still generating
  // its screenshot, or one the client arrows straight past, is never recorded.
  #scheduleFor(slide) {
    this.recorder.cancel();

    if (!this.presentationOpen || !slide?.dataset.designId || !this.#imageOnScreen(slide)) {
      return;
    }

    const { designType, designId } = slide.dataset;
    this.recorder.schedule(designType, designId);
  }

  // A slide rendered with its image already resolved is on screen as soon as it is shown; one
  // waiting on a generated screenshot only counts once designs--lazy-loading has announced it.
  #imageOnScreen(slide) {
    return slide.dataset.designImageReady === 'true' || this.shownSlides.has(slide);
  }
}
